import React, { useEffect, useState } from 'react';
import { getProviders, OAuthProvider, ProviderAvailability } from '../api/oauthApi';
import { startOAuth } from '../auth/oauth';

export default function SocialSignIn({
  destination, intent = 'login', disabled = false, linkedProviders = [],
}: {
  destination: string;
  intent?: 'login' | 'link';
  disabled?: boolean;
  linkedProviders?: OAuthProvider[];
}) {
  const [providers, setProviders] = useState<ProviderAvailability | null>(null);
  const [availabilityError, setAvailabilityError] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 10000);
    setProviders(null);
    setAvailabilityError(false);
    getProviders(controller.signal).then((value) => {
      if (active) setProviders(value);
    }).catch(() => {
      if (active) setAvailabilityError(true);
    }).finally(() => window.clearTimeout(timeout));
    return () => { active = false; controller.abort(); window.clearTimeout(timeout); };
  }, [attempt]);

  const begin = async (provider: OAuthProvider) => {
    setBusy(true);
    setError(null);
    try {
      await startOAuth(provider, destination, intent);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Unable to start sign-in. Please try again.');
      setBusy(false);
    }
  };

  return (
    <section aria-label={intent === 'link' ? 'Link a sign-in provider' : 'Social sign-in'}>
      {!providers && !availabilityError && <p role="status">Checking social sign-in availability…</p>}
      {availabilityError && <p role="status">
        Social sign-in is unavailable because the sign-in service could not be reached. You can still use email and password.
        {' '}<button className="btn btn--ghost btn--sm" onClick={() => setAttempt((value) => value + 1)}>Retry availability</button>
      </p>}
      {(['google', 'apple'] as const).filter((provider) => !linkedProviders.includes(provider)).map((provider) => {
        const label = provider === 'google' ? 'Google' : 'Apple';
        const enabled = providers?.[provider]?.enabled === true;
        return <button key={provider} className="btn btn--ghost btn--block btn--lg"
          disabled={!enabled || busy || disabled} onClick={() => begin(provider)}>
          {intent === 'link' ? 'Link' : 'Continue with'} {label}{providers && !enabled ? ' (unavailable)' : ''}
        </button>;
      })}
      {busy && <p role="status">Redirecting to the sign-in provider…</p>}
      {error && <p role="alert" className="auth__error">{error}</p>}
    </section>
  );
}
